import React, { useEffect, useRef, useState } from 'react';
import {
  Home, Clock, ClipboardCheck, Shield, DollarSign, CheckCircle2,
  ArrowRight, LucideIcon, RefreshCw,
} from 'lucide-react';
import './HomeV2.css';
import { useSiteContext } from '../../SiteContext';
import HomeManager from '../../managers/HomeManager';
import EmployeeManager from '../../managers/EmployeeManager';
import config from '../../configLoader';
import { Page } from '../../interfaces/PagesInterface';
import { EmployeeSearchResult } from '../../interfaces/EmployeeInterface';
import { bucketLabel, type StatusKey } from '../EmployeeTimesheetReviewConsole';
import { mapSearchStatus } from '../../util/employeeSearchStatus';
import { can, isSuperAdmin } from '../../util/adminPermissions';
import { getCrewLiveStatus, getCrewSupervisors } from '../../api/crewLiveStatus';
import { CrewMemberStatus, CrewSupervisor } from '../../interfaces/CrewLiveStatus';
import CrewLiveStatusCard from './CrewLiveStatusCard';
import CrewMapCard from './CrewMapCard';
import { fullName } from './crewUtil';

/** NPM's two-level approval — the tenants that show a 1st Approval stage. */
const managerApprovalOn = config.allowManagerApproval === true;

/** Persists the Super Admin's crew-picker selection across sessions (this browser). */
const CREW_SUP_KEY = 'homev2_crew_supervisor';

interface Counts {
  unsubmitted: number;
  managerPending: number;
  submitted: number;
  supervisorApproved: number;
  payrollApproved: number;
}

const ZERO_COUNTS: Counts = {
  unsubmitted: 0,
  managerPending: 0,
  submitted: 0,
  supervisorApproved: 0,
  payrollApproved: 0,
};

interface StatCard {
  key: string;
  icon: LucideIcon;
  /** Stage-matched ramp (design §6): amber → teal → blue → purple → green, the
   *  same hue the tile's stage wears on the board. */
  tone: 'amber' | 'teal' | 'blue' | 'purple' | 'green';
  value: number;
  label: string;
  sub: string;
  onClick: () => void;
}

const HomeV2: React.FC = () => {
  const {
    userRole, supervisorQueryID, userName, payPeriodID,
    week, startDate, endDate, createPopup,
    setLoading, setPage, setReviewIntent,
  } = useSiteContext();

  const [counts, setCounts] = useState<Counts>(ZERO_COUNTS);
  const [countsLoading, setCountsLoading] = useState<boolean>(true);
  /** Bumped by the Refresh button to re-fetch the stage counts without reloading the page. */
  const [countsNonce, setCountsNonce] = useState<number>(0);

  // Crew widgets (My Crew — Live Status + Crew Map) state.
  const [crew, setCrew] = useState<CrewMemberStatus[]>([]);
  const [crewAsOf, setCrewAsOf] = useState<string | null>(null);
  const [crewLoading, setCrewLoading] = useState<boolean>(false);
  const [crewError, setCrewError] = useState<boolean>(false);
  const [supervisors, setSupervisors] = useState<CrewSupervisor[]>([]);
  const [selSupervisorID, setSelSupervisorID] = useState<number | null>(null);
  /** True once the supervisor picker is resolved (immediately for non-super-admins). */
  const [supReady, setSupReady] = useState<boolean>(false);
  const [selectedCrewEmpID, setSelectedCrewEmpID] = useState<number | null>(null);
  /** Bumped on each crew-row click so repeated clicks re-trigger the map flyTo. */
  const [crewFocusNonce, setCrewFocusNonce] = useState<number>(0);
  /** 30s tick to keep "· {elapsed}" text fresh between 60s polls. */
  const [nowMs, setNowMs] = useState<number>(() => Date.now());

  const showTimesheetCards = can(userRole, 'viewTimesheets');
  const superAdmin = isSuperAdmin(userRole);
  const showCrewWidgets = !!config.showCrewLiveStatus && can(userRole, 'viewTimesheets');

  // Fetch pipeline counts whenever the global week (payPeriodID) changes.
  // TODO(ApprovalAccess spec §15.3.14): GET /admin/timesheets/count is not yet
  // scoped server-side (spec §15.3.14 / not-yet-scoped), so these counts remain
  // org-wide/supervisor-param based. Once the count endpoint is scoped, drop the
  // supervisorQueryID param and rely on session-derived scope. The cards are hidden
  // entirely for admins without ViewTimesheets (below) in the meantime.
  useEffect(() => {
    if (!payPeriodID || !showTimesheetCards) return;
    let cancelled = false;
    const run = async () => {
      setCountsLoading(true);
      try {
        const supervisorId = userRole.supervisor ? supervisorQueryID : null;
        const res = await HomeManager.getTimesheetCounts(payPeriodID, supervisorId as number);
        if (!cancelled) {
          setCounts({
            unsubmitted: res.unsubmitted ?? 0,
            managerPending: res.managerPending ?? 0,
            submitted: res.submitted ?? 0,
            supervisorApproved: res.supervisorApproved ?? 0,
            payrollApproved: res.payrollApproved ?? 0,
          });
        }
      } catch (err) {
        console.error('Error fetching timesheet counts:', err);
      } finally {
        if (!cancelled) setCountsLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [payPeriodID, userRole.supervisor, supervisorQueryID, showTimesheetCards, countsNonce]);

  // ── Crew widgets data ───────────────────────────────────────────────────────

  // Super admins: load the supervisor picker options first (default = first
  // supervisor). Non-super-admins skip straight to their own crew (no param).
  useEffect(() => {
    if (!showCrewWidgets) return;
    if (!superAdmin) { setSupReady(true); return; }
    let cancelled = false;
    const run = async () => {
      try {
        const res = await getCrewSupervisors();
        if (cancelled) return;
        const list = res.supervisors || [];
        setSupervisors(list);
        // Restore the last-viewed supervisor (persisted per browser); fall back
        // to the first supervisor when nothing is saved or the saved one is gone.
        let initial = list.length > 0 ? list[0].employeeID : null;
        try {
          const saved = Number(localStorage.getItem(CREW_SUP_KEY));
          if (saved && list.some((s) => s.employeeID === saved)) initial = saved;
        } catch { /* ignore */ }
        setSelSupervisorID(initial);
      } catch (err) {
        console.error('Error fetching crew supervisors:', err);
      } finally {
        if (!cancelled) setSupReady(true);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [showCrewWidgets, superAdmin]);

  // Live-status fetch + 60s poll while Home is mounted. The interval is cleared
  // on unmount / supervisor change; polls are skipped while the tab is hidden.
  // crewLoadRef exposes the loader to the manual Refresh button. Only local
  // useState setters + the module-level API fn are referenced (SiteContext fn
  // refs are unstable across renders — repo gotcha — so they must never be
  // deps of a data-loading effect).
  const crewLoadRef = useRef<((silent: boolean) => void) | null>(null);
  const crewReqRef = useRef(0);
  useEffect(() => {
    if (!showCrewWidgets || !supReady) return;
    let cancelled = false;
    const load = async (silent: boolean) => {
      const reqID = ++crewReqRef.current;
      if (!silent) setCrewLoading(true);
      try {
        const res = await getCrewLiveStatus(selSupervisorID ?? undefined);
        if (cancelled || reqID !== crewReqRef.current) return;
        setCrew(res.employees || []);
        setCrewAsOf(res.asOf || null);
        setCrewError(false);
        setNowMs(Date.now());
      } catch (err) {
        console.error('Error fetching crew live status:', err);
        if (!cancelled && reqID === crewReqRef.current) setCrewError(true);
      } finally {
        if (!cancelled && reqID === crewReqRef.current && !silent) setCrewLoading(false);
      }
    };
    crewLoadRef.current = load;
    load(false);
    const timer = window.setInterval(() => {
      if (!document.hidden) load(true);
    }, 60000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      crewLoadRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCrewWidgets, supReady, selSupervisorID]);

  // 30s re-render tick so the "since {time} · {elapsed}" text stays current.
  useEffect(() => {
    if (!showCrewWidgets) return;
    const timer = window.setInterval(() => setNowMs(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, [showCrewWidgets]);

  const handleSupervisorChange = (employeeID: number) => {
    setSelectedCrewEmpID(null);
    setSelSupervisorID(employeeID);
    try { localStorage.setItem(CREW_SUP_KEY, String(employeeID)); } catch { /* quota — ignore */ }
  };

  const handleCrewRefresh = () => { crewLoadRef.current?.(false); };

  // Crew-row click: highlight the row and point the mini map at the employee's
  // site (clocked-out rows just highlight — CrewMapCard resets the view).
  const handleCrewRowClick = (m: CrewMemberStatus) => {
    setSelectedCrewEmpID(m.employeeID);
    setCrewFocusNonce((n) => n + 1);
  };

  const clearCrewSelection = () => setSelectedCrewEmpID(null);

  const selectedCrewEmployee =
    crew.find((c) => c.employeeID === selectedCrewEmpID) || null;

  const selSupervisor = supervisors.find((s) => s.employeeID === selSupervisorID) || null;
  const crewTitle = superAdmin && selSupervisor
    ? `${selSupervisor.name.first}'s Crew — Live Status`
    : 'My Crew — Live Status';

  const firstName = (userName || '').trim().split(/\s+/)[0] || '';

  // Subtitle date range: strip leading zeros (7/5/2026, not 07/05/2026) and drop the
  // redundant year from the start when both dates share it (design format "6/22 – 6/28/2026").
  const unpadDate = (s: string): string => {
    const p = s.split('/');
    return p.length === 3 ? `${parseInt(p[0], 10)}/${parseInt(p[1], 10)}/${p[2]}` : s;
  };
  const sameYear = !!startDate && !!endDate && startDate.slice(6) === endDate.slice(6);
  const endDisplay = unpadDate(endDate);
  const startDisplay = sameYear
    ? unpadDate(startDate).split('/').slice(0, 2).join('/') // month/day only
    : unpadDate(startDate);

  /** Navigate to the v2 board with a bucket preselected. */
  const goToBucket = (bucket: StatusKey) => {
    setReviewIntent({ bucket });
    setPage(Page.TimesheetReview);
  };

  // Build the stat-card set. 4 cards; 5 when the tenant allows manager approval.
  // Every tile drills into its own stage on the v2 board — the Manager Pending
  // tile's old escape to the v1 SupervisorList is gone now that the board has a
  // managerPending stage. Labels come from the shared translator-driven bucket
  // vocabulary, so NPM reads "1st / 2nd Approval Pending" and everyone else the
  // defaults; only the Unsubmitted tile keeps its longer Home-specific wording.
  const statCards: StatCard[] = [];
  statCards.push({
    key: 'unsubmitted', icon: Clock, tone: 'amber', value: counts.unsubmitted,
    label: 'Unsubmitted Timesheets', sub: 'awaiting employee submit',
    onClick: () => goToBucket('unsubmitted'),
  });
  if (managerApprovalOn) {
    statCards.push({
      key: 'managerPending', icon: ClipboardCheck, tone: 'teal', value: counts.managerPending,
      label: bucketLabel('managerPending'), sub: 'needs manager review',
      onClick: () => goToBucket('managerPending'),
    });
  }
  statCards.push({
    key: 'submitted', icon: Shield, tone: 'blue', value: counts.submitted,
    label: bucketLabel('submitted'), sub: 'needs supervisor review',
    onClick: () => goToBucket('submitted'),
  });
  statCards.push({
    key: 'supervisorApproved', icon: DollarSign, tone: 'purple', value: counts.supervisorApproved,
    label: bucketLabel('supervisorApproved'), sub: 'queued for payroll',
    onClick: () => goToBucket('supervisorApproved'),
  });
  statCards.push({
    key: 'payrollApproved', icon: CheckCircle2, tone: 'green', value: counts.payrollApproved,
    label: bucketLabel('payrollApproved'), sub: 'finalized this week',
    onClick: () => goToBucket('payrollApproved'),
  });

  // Employee search moved to the global header (components/v2/GlobalEmployeeSearch),
  // so it works from every page rather than only this one.

  // "View timesheet" from the crew map popup — exact altEmployeeID search, then the
  // same mapSearchStatus → reviewIntent hop the header search makes.
  const openTimesheetFromMap = async (m: CrewMemberStatus) => {
    if (!payPeriodID) return;
    setLoading(true);
    try {
      const response = await EmployeeManager.searchEmployees(m.altEmployeeID, payPeriodID);
      const results: EmployeeSearchResult[] = response.data || [];
      const match = results.find((r) => r.employeeID === m.employeeID);
      const mapped = match ? mapSearchStatus(match.timesheetStatus) : null;
      if (!match || !mapped) {
        createPopup(
          'No Timesheet',
          `${fullName(m)} has no reviewable timesheet for Week ${week}.`,
        );
        return;
      }
      setReviewIntent({
        bucket: mapped.bucket,
        employeeID: match.employeeID,
        altEmployeeID: match.altEmployeeID,
        name: match.name,
      });
      setPage(Page.TimesheetReview);
    } catch (err) {
      console.error('Error opening employee timesheet from crew map:', err);
      createPopup('Search Error', 'Failed to open the employee timesheet. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="homev2">
      {/* Page head */}
      <div className="page-head">
        <div className="ph-ic"><Home /></div>
        <div>
          <h1>Welcome, {firstName}</h1>
          <div className="ph-sub">
            Week {week} · {startDisplay} – {endDisplay} — here's where timesheets stand.
          </div>
        </div>
        {showTimesheetCards && (
          <button
            type="button"
            className="btn ph-refresh"
            onClick={() => setCountsNonce((n) => n + 1)}
            disabled={countsLoading}
            title="Refresh the timesheet counts"
          >
            <RefreshCw className={countsLoading ? 'spin' : undefined} />
            Refresh
          </button>
        )}
      </div>

      {/* Stat cards — hidden entirely for admins without ViewTimesheets (spec §15.3.14). */}
      {showTimesheetCards && (
        <div
          className="dash-grid"
          style={{ gridTemplateColumns: `repeat(${statCards.length}, minmax(0, 1fr))` }}
        >
          {statCards.map((c) => {
            const Icon = c.icon;
            return (
              <button key={c.key} className="statcard" onClick={c.onClick} type="button">
                <div className={`si ${c.tone}`}><Icon /></div>
                <div className="sv">
                  {countsLoading ? <span className={`sv-spin ${c.tone}`} aria-label="Loading" role="status" /> : c.value}
                </div>
                <div className="sl">{c.label}</div>
                <div className="sd">{c.sub}</div>
                <span className="arrow"><ArrowRight /></span>
              </button>
            );
          })}
        </div>
      )}

      {/* Crew widgets — showCrewLiveStatus tenants with ViewTimesheets only */}
      {showCrewWidgets && (
        <div className="home-two">
          <CrewLiveStatusCard
            title={crewTitle}
            crew={crew}
            loading={crewLoading}
            error={crewError}
            showSupervisorPicker={superAdmin}
            supervisors={supervisors}
            selectedSupervisorID={selSupervisorID}
            onSupervisorChange={handleSupervisorChange}
            onRefresh={handleCrewRefresh}
            selectedEmployeeID={selectedCrewEmpID}
            onRowClick={handleCrewRowClick}
            nowMs={nowMs}
          />
          <CrewMapCard
            crew={crew}
            asOf={crewAsOf}
            selectedEmployee={selectedCrewEmployee}
            focusNonce={crewFocusNonce}
            onClearSelection={clearCrewSelection}
            onViewTimesheet={openTimesheetFromMap}
            nowMs={nowMs}
          />
        </div>
      )}

    </div>
  );
};

export default HomeV2;
